import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BiSearch, BiPlus, BiEdit, BiShow, BiRefresh, BiPrinter, BiUndo, BiSpreadsheet } from "react-icons/bi";
import { toast } from "react-toastify";

import { getJrList, recallJr, jrV2Selector } from "./jrV2Slice";
import GenerateGsModal from "./GenerateGsModal";
import { Colors } from "../../../assets/Colors";

const STATUS_CONFIG = {
  "-1":  { label: "Draft",             bg: "bg-gray-100",   text: "text-gray-600",   dot: "bg-gray-400"    },
  "99":  { label: "Menunggu Manifest", bg: "bg-blue-100",   text: "text-blue-700",   dot: "bg-blue-500"    },
  "100": { label: "Menunggu Activity", bg: "bg-indigo-100", text: "text-indigo-700", dot: "bg-indigo-500"  },
  "110": { label: "Revisi JR",         bg: "bg-amber-100",  text: "text-amber-700",  dot: "bg-amber-500"   },
  "150": { label: "Menunggu Approval", bg: "bg-purple-100", text: "text-purple-700", dot: "bg-purple-500"  },
  "199": { label: "Pending Client",    bg: "bg-yellow-100", text: "text-yellow-700", dot: "bg-yellow-500"  },
  "200": { label: "Approved",          bg: "bg-green-100",  text: "text-green-700",  dot: "bg-green-500"   },
  "201": { label: "Partial Reject",    bg: "bg-red-100",    text: "text-red-700",    dot: "bg-red-500"     },
};

const TABS = [
  { key: "",    label: "Semua" },
  { key: "-1",  label: "Draft" },
  { key: "99",  label: "Menunggu Manifest" },
  { key: "100", label: "Menunggu Activity" },
  { key: "110", label: "Revisi JR" },
  { key: "150", label: "Menunggu Approval" },
  { key: "199", label: "Pending Client" },
  { key: "200", label: "Approved" },
  { key: "201", label: "Partial Reject" },
];

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[String(status)] ?? STATUS_CONFIG["-1"];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

export default function JobRequestMonitoringV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const jrV2 = useSelector(jrV2Selector);

  const [activeTab, setActiveTab] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [klienFilter, setKlienFilter] = useState(""); // KlienID, "" = semua
  const [jenisJr, setJenisJr] = useState("OPS"); // OPS | GS
  const [showGenerate, setShowGenerate] = useState(false);
  const [recallRow, setRecallRow]       = useState(null); // JR yang sedang di-recall
  const [recallReason, setRecallReason] = useState("");
  const [recalling, setRecalling]       = useState(false);

  const isGS = jenisJr === "GS";

  // Path navigasi menyesuaikan tipe user (client vs RKS)
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isClient = currentUser?.Tipe === "Customer";
  const createPath = isClient ? "/create-job-Request" : "/operasional-job-request-form";
  const editPath   = isClient ? "/create-job-Request" : "/operasional-job-request-edit";
  const detailPath = "/operasional-job-request-detail-v2";

  // Ambil daftar tiap kali jenis JR / pencarian berubah
  useEffect(() => {
    const t = setTimeout(() => {
      dispatch(getJrList({ search: inputValue, JrType: jenisJr }));
    }, 300);
    return () => clearTimeout(t);
  }, [inputValue, jenisJr]); // eslint-disable-line

  // Ganti jenis JR → reset tab status
  const switchJenis = (j) => { setJenisJr(j); setActiveTab(""); };

  const handleGenerated = () => {
    setShowGenerate(false);
    dispatch(getJrList({ search: inputValue, JrType: "GS" }));
  };

  // Pilihan klien dirakit dari daftar JR yang sudah dimuat (jenis JR + pencarian
  // aktif), jadi hanya klien yang memang punya JR yang muncul.
  const klienOptions = useMemo(() => {
    const m = new Map();
    (jrV2.list ?? []).forEach(r => {
      if (r.KlienID && !m.has(r.KlienID)) m.set(r.KlienID, r.NamaKlien || r.KlienID);
    });
    return [...m].map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [jrV2.list]);

  // Filter klien berlaku sebelum tab status, agar jumlah di tiap tab ikut menyempit.
  const byKlien = (jrV2.list ?? []).filter(r => !klienFilter || r.KlienID === klienFilter);

  const filtered = byKlien.filter(r =>
    !activeTab || String(r.Status) === activeTab);

  const countByStatus = byKlien.reduce((acc, r) => {
    acc[String(r.Status)] = (acc[String(r.Status)] ?? 0) + 1;
    return acc;
  }, {});

  const activityPath = "/operasional-activity-detail-v2";

  // Recall: JR Approved → Revisi JR (versi naik), lalu langsung ke halaman Activity.
  const handleRecall = async () => {
    if (!recallReason.trim()) { toast.error("Alasan recall wajib diisi"); return; }
    setRecalling(true);
    try {
      const res = await dispatch(recallJr({ JrID: recallRow.JrID, Reason: recallReason.trim() })).unwrap();
      toast.success(res?.message ?? "JR di-recall");
      const jrID = recallRow.JrID;
      setRecallRow(null);
      navigate(activityPath, { state: { JrID: jrID } });
    } catch (e) {
      toast.error(e?.message ?? "Gagal recall JR");
    } finally {
      setRecalling(false);
    }
  };

  // MJS (hanya Usage) untuk quotation JR ini, bulan default = bulan JR.
  const openMjs = (row) => {
    let month = "";
    if (isGS && /^\d{4}-\d{2}/.test(row.Period || "")) month = row.Period.slice(0, 7);
    else {
      const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(row.WorkDate || ""); // DD-MM-YYYY
      if (m) month = `${m[3]}-${m[2]}`;
    }
    navigate("/mjs", { state: {
      QuatationNo: row.QoNo,
      isV2: true,
      usageOnly: true,
      backTo: "/operasional-job-request-list",
      month,
      header: {
        NoKontrak: row.NoKontrak,
        NamaKlien: row.NamaKlien,
        KlienID: row.KlienID,
      },
    } });
  };

  const goDetail = (row) => {
    if (row.Status === 110 && !isClient) {
      navigate(activityPath, { state: { JrID: row.JrID } });
    } else if (row.Status === -1) {
      navigate(editPath, { state: { JrID: row.JrID } });
    } else {
      navigate(detailPath, { state: { JrID: row.JrID } });
    }
  };

  return (
    <div className="md:ml-64">
      <div style={{ background: Colors.primary }} className="pt-14 pb-20 px-8" />

      <div className="px-4 md:px-8 -mt-16">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">

          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-bold text-gray-800">Daftar Job Request</h2>
              {/* Switch jenis JR */}
              <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
                {[{ k: "OPS", l: "Operasional" }, { k: "GS", l: "General Service" }].map(o => (
                  <button key={o.k} onClick={() => switchJenis(o.k)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                      jenisJr === o.k ? "bg-white shadow text-orange-600" : "text-gray-500 hover:text-gray-700"}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Aksi: OPS = buat JR baru; GS = generate per periode */}
            {!isGS ? (
              <button
                onClick={() => navigate(createPath)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold"
                style={{ background: Colors.primary }}>
                <BiPlus className="text-lg" /> Buat JR Baru
              </button>
            ) : (
              <button
                onClick={() => setShowGenerate(true)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold"
                style={{ background: Colors.primary }}>
                <BiRefresh className="text-lg" /> Generate JR General Service
              </button>
            )}
          </div>

          {/* Search */}
          <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b bg-gray-50">
            <div className="relative w-full max-w-sm">
              <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input type="text" value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder="Cari JrID, no. QO, atau nama klien…"
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg outline-none" />
            </div>
            {/* Client hanya melihat JR miliknya sendiri — filter klien tak berguna baginya. */}
            {!isClient && (
              <select value={klienFilter} onChange={e => setKlienFilter(e.target.value)}
                className="w-full sm:w-64 px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none bg-white">
                <option value="">Semua Klien</option>
                {klienFilter && !klienOptions.some(o => o.value === klienFilter) && (
                  <option value={klienFilter}>{klienFilter}</option>
                )}
                {klienOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
          </div>

          {/* Tabs */}
          <div className="flex border-b overflow-x-auto">
            {TABS.map(tab => {
              const count = tab.key ? (countByStatus[tab.key] ?? 0) : byKlien.length;
              const active = activeTab === tab.key;
              return (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
                    active ? "border-orange-500 text-orange-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
                  {tab.label}
                  {count > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                      active ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-500"}`}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {jrV2.isLoading ? (
              <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Memuat data…</div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <span className="text-4xl mb-3">📋</span>
                <p className="text-sm">{isGS ? "Belum ada JR General Service" : "Belum ada Job Request"}</p>
                {isGS ? (
                  <p className="mt-2 text-xs text-gray-400">Klik “Generate JR General Service” lalu pilih periode, quotation, dan CQ.</p>
                ) : (
                  <button onClick={() => navigate(createPath)}
                    className="mt-3 text-xs text-orange-500 underline">Buat JR pertama</button>
                )}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-5 py-3 font-semibold text-gray-600">No. JR</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">No. QO</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">Nama Klien</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">CQ</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">{isGS ? "Periode" : "Tgl Kerja"}</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">SPK No</th>
                    <th className="px-5 py-3 font-semibold text-gray-600">Status</th>
                    <th className="px-5 py-3 font-semibold text-gray-600 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(row => (
                    <tr key={row.JrID} className="hover:bg-orange-50 cursor-pointer" onClick={() => goDetail(row)}>
                      <td className="px-5 py-3.5 font-mono text-xs text-gray-700 whitespace-nowrap">
                        {row.JrID}
                        {row.Revision > 0 && (
                          <span title={`Versi ${row.Revision} (sudah ${row.Revision}x di-recall)`}
                            className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-sans font-semibold bg-amber-100 text-amber-700">
                            Rev {row.Revision}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 font-mono text-xs text-gray-600">{row.QoNo}</td>
                      <td className="px-5 py-3.5 text-gray-700 max-w-[180px] truncate">{row.NamaKlien || row.KlienID}</td>
                      <td className="px-5 py-3.5 text-gray-700 max-w-[200px]">
                        {row.KodeCQ ? (
                          <div className="truncate" title={row.NamaCQ ? `${row.KodeCQ} — ${row.NamaCQ}` : row.KodeCQ}>
                            <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700">{row.KodeCQ}</span>
                            {row.NamaCQ && <span className="ml-1.5 text-gray-600">{row.NamaCQ}</span>}
                          </div>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-5 py-3.5 text-gray-700 whitespace-nowrap">{isGS ? (row.Period || "—") : row.WorkDate}</td>
                      <td className="px-5 py-3.5 text-gray-600">{row.SPKNo || <span className="text-gray-400">—</span>}</td>
                      <td className="px-5 py-3.5">
                        <StatusBadge status={row.Status} />
                        {/* Menunggu Approval: siapa yang sedang ditunggu (approver grup
                            JobRequest di Setup › Approval, urutan berikutnya). */}
                        {row.Status === 150 && (row.MenungguNama || row.ApprovalTotal > 0) && (
                          <div className="mt-1 text-[11px] leading-tight text-purple-700"
                            title={row.MenungguNik ? `${row.MenungguNama} (${row.MenungguNik})` : undefined}>
                            Menunggu: <span className="font-semibold">{row.MenungguNama || "approver belum diatur"}</span>
                            {row.ApprovalTotal > 1 && <span className="text-purple-500"> · {row.ApprovalKe}/{row.ApprovalTotal}</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right space-x-1.5" onClick={e => e.stopPropagation()}>
                        {/* Print setelah approval internal RKS selesai (approver di
                            Setup > Approval grup JobRequest, mis. Fatchul) — status 199
                            Pending Client — dan setelah disetujui client (200). Draft,
                            Menunggu Approval, Revisi & Partial Reject tidak bisa dicetak
                            karena isinya masih bisa berubah. */}
                        {(row.Status === 199 || row.Status === 200) && (
                          <button onClick={() => navigate("/cetak-jr-v2", { state: { JrID: row.JrID } })}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-blue-600 border-blue-300 hover:bg-blue-50">
                            <BiPrinter className="text-sm" /> Print JR
                          </button>
                        )}
                        {row.QoNo && (
                          <button onClick={() => openMjs(row)}
                            title="Lihat MJS (usage harian) quotation JR ini"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-indigo-600 border-indigo-300 hover:bg-indigo-50">
                            <BiSpreadsheet className="text-sm" /> MJS
                          </button>
                        )}
                        {/* Recall hanya untuk RKS (bukan client) & JR Approved */}
                        {row.Status === 200 && !isClient && (
                          <button onClick={() => { setRecallRow(row); setRecallReason(""); }}
                            title="Tarik kembali JR untuk merevisi activity"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-amber-700 border-amber-300 hover:bg-amber-50">
                            <BiUndo className="text-sm" /> Recall
                          </button>
                        )}
                        {row.Status === -1 ? (
                          <button onClick={() => navigate(editPath, { state: { JrID: row.JrID } })}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-orange-600 border-orange-300 hover:bg-orange-50">
                            <BiEdit className="text-sm" /> Edit
                          </button>
                        ) : (
                          <button onClick={() => navigate(detailPath, { state: { JrID: row.JrID } })}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 border-gray-300 hover:bg-gray-50">
                            <BiShow className="text-sm" /> Detail
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {recallRow && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
              onClick={() => !recalling && setRecallRow(null)}>
              <div className="w-full max-w-md bg-white rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
                <div className="px-6 py-4 border-b">
                  <h3 className="text-lg font-bold text-gray-800">Recall JR {recallRow.JrID}</h3>
                </div>
                <div className="px-6 py-5 space-y-3 text-sm">
                  <p className="text-gray-600">
                    JR akan ditarik dari status <strong>Approved</strong> menjadi <strong>Revisi JR</strong>
                    {" "}(versi {(recallRow.Revision ?? 0) + 1}). Activity bisa diedit lalu disubmit ulang, dan
                    harus melewati approval internal serta persetujuan client lagi. Isi versi saat ini diarsipkan.
                  </p>
                  <div>
                    <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
                      Alasan Recall <span className="text-red-500">*</span>
                    </label>
                    <textarea rows={3} value={recallReason} onChange={e => setRecallReason(e.target.value)}
                      className="w-full border rounded px-3 py-2 text-sm outline-none" autoFocus
                      placeholder="Mis. qty forklift salah input, seharusnya 6 jam" />
                  </div>
                </div>
                <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
                  <button onClick={() => setRecallRow(null)} disabled={recalling}
                    className="px-4 py-2 rounded-lg border text-sm font-medium text-gray-600 hover:bg-gray-100">Batal</button>
                  <button onClick={handleRecall} disabled={recalling}
                    className="px-4 py-2 rounded-lg text-white text-sm font-semibold bg-amber-600 hover:bg-amber-700 disabled:opacity-50">
                    {recalling ? "Memproses…" : "Recall"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showGenerate && (
            <GenerateGsModal
              initialPeriod={new Date().toISOString().slice(0, 7)}
              onClose={() => setShowGenerate(false)}
              onGenerated={handleGenerated} />
          )}

          {!jrV2.isLoading && filtered.length > 0 && (
            <div className="px-6 py-3 border-t bg-gray-50 text-xs text-gray-500">
              Menampilkan <strong>{filtered.length}</strong> dari <strong>{(jrV2.list ?? []).length}</strong> Job Request
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
